import {ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Flex} from '@chakra-ui/react';
import {slimScrollbar} from '../../components/ui/scrollbar';

export type ViewerPanelSide = 'left' | 'right';

/** Where a panel lives: a desktop side, by an id unique among the viewer's panels. */
export interface ViewerPanelSlot {
    id: string;
    side: ViewerPanelSide;
}

/**
 * A module of the viewer's sidebars. From `md` up each side shows one of its panels at a time,
 * picked by its toggle in the top bar; on phones one sheet under the picture shows every panel.
 */
export interface ViewerPanel extends ViewerPanelSlot {
    /** Its region's accessible name. */
    title: string;
    /** The toggle's accessible name. */
    toggleLabel: string;
    /** The toggle's hover text. */
    toggleTitle?: string;
    icon: ReactNode;
    /** A count shown on the toggle (e.g. open comments). */
    badge?: number;
    /** `KeyboardEvent.key` values (lower case) that toggle it. */
    keys?: string[];
    /** Wheter to fill the sidebar's height and scroll its content itself, rather than the whole sidebar scrolling. */
    fill?: boolean;
    content: ReactNode;
}

export const INFO_PANEL: ViewerPanelSlot = {id: 'info', side: 'right'};
export const COMMENTS_PANEL: ViewerPanelSlot = {id: 'comments', side: 'left'};

interface OpenPanels {
    left: string | null;
    right: string | null;
    /** The phones' single sheet. */
    sheet: boolean;
}

const STORAGE_KEY = 'spot-drive-viewer-panels';
const CLOSED: OpenPanels = {left: null, right: null, sheet: false};

function loadOpenPanels(): OpenPanels {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
        if (!stored || typeof stored !== 'object') return CLOSED;
        return {
            left: typeof stored.left === 'string' ? stored.left : null,
            right: typeof stored.right === 'string' ? stored.right : null,
            sheet: stored.sheet === true,
        };
    } catch {
        return CLOSED;
    }
}

function saveOpenPanels(panels: OpenPanels) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(panels));
    } catch {
        // Not remembered then (e.g. storage blocked).
    }
}

export interface ViewerPanelState {
    isShown: (slot: ViewerPanelSlot) => boolean;
    /** The panel shown on a side from `md` up. */
    openOn: (side: ViewerPanelSide) => string | null;
    toggle: (slot: ViewerPanelSlot) => void;
    sheetOpen: boolean;
    toggleSheet: () => void;
}

/** Which panels are open, remembered in sessionStorage: for the tab's session, across viewer openings. */
export function useViewerPanelState(isMobile: boolean): ViewerPanelState {
    const [open, setOpen] = useState(loadOpenPanels);

    function update(next: OpenPanels) {
        setOpen(next);
        saveOpenPanels(next);
    }

    return {
        isShown: (slot) => isMobile ? open.sheet : open[slot.side] === slot.id,
        openOn: (side) => open[side],
        toggle: (slot) => update({...open, [slot.side]: open[slot.side] === slot.id ? null : slot.id}),
        sheetOpen: open.sheet,
        toggleSheet: () => update({...open, sheet: !open.sheet}),
    };
}

// Height of the sheet that stacks under the picture on phones.
const SHEET_HEIGHT = '45vh';
const SIDEBAR_WIDTH = '320px';
const SLIDE_MS = 120;

interface ViewerSidebarProps {
    /** Its panels; while it slides closed, it keeps showing the last ones. */
    panels: ViewerPanel[];
    /** A desktop side, or the phones' sheet with every panel. */
    placement: ViewerPanelSide | 'sheet';
    open: boolean;
}

/**
 * A sidebar of the viewer: a side's open panel from `md` up, or every panel stacked on phones. It
 * slides open and closed by growing from and shrinking to nothing, so the picture beside it resizes
 * along; its content keeps its full size and moves in from the edge next to the picture.
 */
export function ViewerSidebar({panels, placement, open}: ViewerSidebarProps) {
    const isSheet = placement === 'sheet';
    const lastPanels = useRef(panels);
    if (panels.length > 0) lastPanels.current = panels;
    const shownPanels = panels.length > 0 ? panels : lastPanels.current;
    // Mounted from opening until it has slid closed; expanded while (becoming) open. One open from
    // the start (remembered from before) is there at once, without sliding.
    const [mounted, setMounted] = useState(open);
    const [expanded, setExpanded] = useState(open);
    if (open && !mounted) setMounted(true);
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (open && !expanded && ref.current) {
            // Lays it out collapsed first, so expanding is a transition rather than a jump.
            void ref.current.offsetWidth;
            setExpanded(true);
        } else if (!open && expanded) {
            setExpanded(false);
        }
    }, [open, expanded, mounted]);

    useEffect(() => {
        if (open || !mounted) return;
        // A timer rather than `transitionend`, which never comes without a transition (reduced motion).
        const timer = window.setTimeout(() => setMounted(false), SLIDE_MS + 50);
        return () => window.clearTimeout(timer);
    }, [open, mounted]);

    if (!mounted || shownPanels.length === 0) return null;
    const filled = !isSheet && shownPanels.some((panel) => panel.fill);
    return <Flex
        ref={ref}
        flexShrink={0}
        overflow="hidden"
        // The content hangs from the edge next to the picture, so it slides in with that edge.
        justify={placement === 'left' ? 'flex-end' : 'flex-start'}
        w={isSheet ? '100%' : expanded ? SIDEBAR_WIDTH : 0}
        h={isSheet ? (expanded ? SHEET_HEIGHT : 0) : '100%'}
        transition={`${isSheet ? 'height' : 'width'} ${SLIDE_MS}ms ease-out`}
        _motionReduce={{transition: 'none'}}
        borderColor="whiteAlpha.200"
        borderTopWidth={isSheet ? '1px' : 0}
        borderLeftWidth={placement === 'right' ? '1px' : 0}
        borderRightWidth={placement === 'left' ? '1px' : 0}
        data-viewer-sidebar={placement}
        data-state={open ? 'open' : 'closed'}
    >
        <Flex
            direction="column"
            bg="gray.900"
            color="white"
            w={isSheet ? '100%' : SIDEBAR_WIDTH}
            h={isSheet ? SHEET_HEIGHT : '100%'}
            flexShrink={0}
            overflowY={filled ? 'hidden' : 'auto'}
            css={slimScrollbar}
        >
            {/* Stacked panels are parted by a line; each has headings of its own. The last one fills the
                rest, so e.g. the comments' composer stays at the bottom. */}
            {shownPanels.map((panel, i) => <Flex
                key={panel.id}
                as="section"
                aria-label={panel.title}
                direction="column"
                flexShrink={filled && panel.fill ? 1 : 0}
                minH={filled && panel.fill ? 0 : undefined}
                flexGrow={i === shownPanels.length - 1 ? 1 : 0}
                borderTopWidth={i > 0 ? '1px' : 0}
                borderColor="whiteAlpha.200"
            >
                {panel.content}
            </Flex>)}
        </Flex>
    </Flex>;
}
